import type { Option } from '@/modules/common/types/option.type.ts';
import type { Segment } from '@/modules/counterparty/types/segment.type.ts';
import type { Region } from '@/modules/counterparty/types/region.type.ts';
import type { DirectoryField } from '@/modules/counterparty/counterparty-master/consts/directory.const.ts';

const pendingSegments = new Map<number, Segment>();
const pendingRegions = new Map<number, Region>();

let nextTempId = -1;

export function isPendingDirectoryId(id: number) {
  return Number.isFinite(id) && id < 0;
}

export function createPendingDirectoryId() {
  const id = nextTempId;
  nextTempId -= 1;
  return id;
}

export function getPendingDirectoryList(field: DirectoryField) {
  return field === 'segment' ? [...pendingSegments.values()] : [...pendingRegions.values()];
}

export function getPendingSegment(id: number) {
  return pendingSegments.get(id);
}

export function getPendingRegion(id: number) {
  return pendingRegions.get(id);
}

export function getPendingDirectoryOptions(field: DirectoryField): Option<number>[] {
  return getPendingDirectoryList(field).map((entry) => ({ code: entry.id, label: entry.name }));
}

export function upsertPendingSegment(id: number, name: string, note = '') {
  pendingSegments.set(id, { id, name, note: note || null });
}

export function upsertPendingRegion(id: number, name: string, tzId: number, center = '', note = '') {
  pendingRegions.set(id, { id, name, tzId, center, note: note || null });
}

export function removePendingDirectory(field: DirectoryField, id: number) {
  if (field === 'segment') {
    pendingSegments.delete(id);
    return;
  }

  pendingRegions.delete(id);
}

export function clearPendingDirectory() {
  pendingSegments.clear();
  pendingRegions.clear();
  nextTempId = -1;
}

export function pruneUnusedPendingDirectory(field: DirectoryField, keepId: number | null) {
  let removed = false;

  for (const entry of getPendingDirectoryList(field)) {
    if (keepId === null || entry.id !== keepId) {
      removePendingDirectory(field, entry.id);
      removed = true;
    }
  }

  return removed;
}

export function mergeDirectoryOptions(field: DirectoryField, options: Option<number>[]): Option<number>[] {
  const pending = getPendingDirectoryOptions(field);

  if (!pending.length) {
    return options;
  }

  const codes = new Set(options.map((option) => option.code));
  return [...options, ...pending.filter((option) => !codes.has(option.code))];
}
